import { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight01Icon,
  Bookmark01Icon,
  CheckmarkBadge01Icon,
  Comment01Icon,
  CursorMagicSelection01Icon,
  Edit02Icon,
  FavouriteIcon,
  Message01Icon,
  MouseScroll01Icon,
  News01Icon,
  Search01Icon,
  Shield01Icon,
  ThumbsUpIcon,
  UserAdd01Icon,
  UserIcon,
  UserSearch01Icon,
  ViewIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AnimatePresence,
  motion,
  useInView,
  useReducedMotion,
} from "motion/react";

import type { IconSvgElement } from "@hugeicons/react";
import { cn } from "@/common/utils";
import { CheckBullet } from "@/landing/ui/CheckBullet";
import { SectionEyebrow } from "@/landing/ui/SectionEyebrow";
import WabDockIcon from "@/landing/wab/assets/wab-dock-icon.png?url";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const GREEN_TINT = "color-mix(in srgb, var(--green) 12%, transparent)";
const GREEN_TINT_STRONG = "color-mix(in srgb, var(--green) 18%, transparent)";

const AUTO_ADVANCE_MS = 4400;

type StageId = "schema" | "capabilities" | "safety" | "result";

type TabPoint = {
  id: StageId;
  num: string;
  title: string;
  body: string;
};

const POINTS: TabPoint[] = [
  {
    id: "schema",
    num: "01",
    title: "Wonda Automated Browser",
    body: "The #1 antidetect browser for social media. Designed to feel like you so you can safely automate in your voice. Agent-native from day 1.",
  },
  {
    id: "capabilities",
    num: "02",
    title: "Opens up everything you do manually",
    body: "Extract any info, message, reaction, scroll. Infinite possibilities.",
  },
  {
    id: "safety",
    num: "03",
    title: "Safety caps built in",
    body: "Avoid spam, protect your account, and leave the world in a better place.",
  },
  {
    id: "result",
    num: "04",
    title: "Beat your competition",
    body: "Win with GTM flows tailored to you, not some rigid template.",
  },
];

/* ------------------------------------------------------------------ */
/*  AnimatedNumber — eased rAF counter, reduced-motion aware           */
/* ------------------------------------------------------------------ */

function AnimatedNumber({
  value,
  prefix = "",
  suffix = "",
  duration = 1.3,
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}) {
  const reduced = useReducedMotion();
  const [display, setDisplay] = useState(reduced ? value : 0);

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, reduced]);

  return (
    <span>
      {prefix}
      {display.toLocaleString("en-US")}
      {suffix}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Stage 1 — Bot detection passes (WAB clears every check)            */
/* ------------------------------------------------------------------ */

// The human-pattern scores lead, then the antidetect suites the browser
// clears — every row comes back green. The human list carries 0-100 scores;
// suites read PASS.
//
// Deliberately short and human-first: this stage sells "designed to look
// human", and opening on five suite names (BrowserScan, Pixelscan, WebRTC
// leak) read as a technical audit log to anyone who doesn't already know the
// tools. CreepJS and Sannysoft are the two the audience recognises, so they
// carry the proof; the rest were noise.
const DETECTION_CHECKS = [
  { label: "Scrolls like you", result: "94/100" },
  { label: "Types like you", result: "92/100" },
  { label: "Rests like you", result: "91/100" },
  { label: "CreepJS", result: "Pass" },
  { label: "Sannysoft", result: "Pass" },
];

function SchemaStage({ animate }: { animate: boolean }) {
  return (
    <div className="flex h-full items-center gap-6 p-6 sm:gap-10 sm:p-8">
      {/* WAB icon with green halo + shield badge, tagline beneath */}
      <div className="flex max-w-[13rem] shrink-0 flex-col items-center text-center">
        <div className="relative">
          {animate && (
            <motion.div
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-1/2 size-28 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, color-mix(in srgb, var(--green) 55%, transparent), transparent 68%)",
              }}
              animate={{
                opacity: [0.55, 0.95, 0.55],
                scale: [0.92, 1.04, 0.92],
              }}
              transition={{
                duration: 2.6,
                ease: "easeInOut",
                repeat: Infinity,
              }}
            />
          )}
          <img
            src={WabDockIcon}
            alt="Wonda Automated Browser"
            className="relative size-16 rounded-2xl"
          />
          <span
            className="absolute -right-2 -bottom-2 flex size-7 items-center justify-center rounded-full border"
            style={{
              borderColor: "var(--hair-strong)",
              background: "var(--surface)",
            }}
          >
            <HugeiconsIcon
              icon={Shield01Icon}
              className="size-4 text-[var(--green)]"
              strokeWidth={2}
            />
          </span>
        </div>
        {/* Same treatment as the step-2 caption in HowItWorks (text-xs,
            weight 400, secondary grey) so the two sections read as one. */}
        <p
          className="mt-5 text-xs leading-snug"
          style={{ color: "var(--cli-text-secondary)" }}
        >
          #1 Stealth browser for LinkedIn to keep your accounts safe &amp;
          secure
        </p>
      </div>

      {/* Checks — each row rises into place, then lands its green result */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {DETECTION_CHECKS.map((check, i) => (
          <motion.div
            key={check.label}
            className="flex items-center justify-between gap-2 border px-3 py-1.5"
            style={{
              borderColor: "var(--hair-strong)",
              background: "var(--surface)",
            }}
            initial={animate ? { opacity: 0, y: 22 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.45,
              delay: 0.1 + i * 0.14,
              ease: "easeOut",
            }}
          >
            <span className="flex min-w-0 items-center gap-2">
              <motion.span
                className="flex shrink-0"
                initial={animate ? { opacity: 0, scale: 0.5 } : false}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.25,
                  delay: 0.35 + i * 0.14,
                  ease: "easeOut",
                }}
              >
                <HugeiconsIcon
                  icon={CheckmarkBadge01Icon}
                  className="size-4 text-[var(--green)]"
                  strokeWidth={1.8}
                />
              </motion.span>
              <span
                className="truncate text-xs font-medium tracking-tight sm:text-[13px]"
                style={{ color: "var(--cli-text)" }}
              >
                {check.label}
              </span>
            </span>
            <motion.span
              className="font-mono text-[10px] font-semibold tracking-[0.12em] uppercase"
              style={{ color: "var(--green)" }}
              initial={animate ? { opacity: 0 } : false}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25, delay: 0.35 + i * 0.14 }}
            >
              {check.result}
            </motion.span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stage 2 — Capability grid                                          */
/* ------------------------------------------------------------------ */

type Capability = { icon: IconSvgElement; label: string };

// A command-palette of the real WAB actions — reads up top, writes below.
const CAPABILITIES: Capability[] = [
  { icon: CursorMagicSelection01Icon, label: "Extract info" },
  { icon: Search01Icon, label: "Search" },
  { icon: News01Icon, label: "Read feed" },
  { icon: UserSearch01Icon, label: "Read profile" },
  { icon: MouseScroll01Icon, label: "Scroll" },
  { icon: UserAdd01Icon, label: "Connect" },
  { icon: Message01Icon, label: "Message" },
  { icon: ThumbsUpIcon, label: "Like" },
  { icon: FavouriteIcon, label: "React" },
  { icon: Comment01Icon, label: "Comment" },
  { icon: Edit02Icon, label: "Post" },
  { icon: Bookmark01Icon, label: "Save" },
];

function CapabilitiesStage() {
  return (
    <div className="grid h-full grid-cols-2 gap-px overflow-hidden border border-[var(--hair)] bg-[var(--hair)] sm:grid-cols-3">
      {CAPABILITIES.map((cap, i) => (
        <motion.div
          key={cap.label}
          className="flex items-center gap-2.5 bg-[var(--surface)] px-3.5 py-3"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: i * 0.045, ease: "easeOut" }}
        >
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-full"
            style={{ background: GREEN_TINT }}
          >
            <HugeiconsIcon
              icon={cap.icon}
              className="size-4 text-[var(--green)]"
              strokeWidth={1.7}
            />
          </span>
          <span
            className="text-[13px] font-medium tracking-tight"
            style={{ color: "var(--cli-text)" }}
          >
            {cap.label}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stage 3 — Safety gauge                                             */
/* ------------------------------------------------------------------ */

const SAFETY_POINTS = [
  "No spammy bursts",
  "Human-like pacing between actions",
  "Your account stays protected",
];

type SafetyBar = { label: string; value: number; cap: number };

// Today's activity split by kind — both sit comfortably under their caps.
const SAFETY_BARS: SafetyBar[] = [
  { label: "Reads", value: 12, cap: 40 },
  { label: "Writes", value: 6, cap: 20 },
];

function SafetyStage({ animate }: { animate: boolean }) {
  return (
    <div className="flex h-full flex-col justify-center gap-6 p-6 sm:p-8">
      {/* Shield with pulsing halo + reassurance headline */}
      <div className="flex items-center gap-4">
        <div className="relative flex size-14 shrink-0 items-center justify-center">
          {animate && (
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full"
              style={{ background: GREEN_TINT }}
              animate={{ scale: [1, 1.35, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ duration: 2.4, ease: "easeOut", repeat: Infinity }}
            />
          )}
          <span
            className="relative flex size-12 items-center justify-center rounded-full"
            style={{ background: GREEN_TINT_STRONG }}
          >
            <HugeiconsIcon
              icon={Shield01Icon}
              className="size-6 text-[var(--green)]"
              strokeWidth={1.8}
            />
          </span>
        </div>
        <div>
          <div
            className="text-lg font-semibold tracking-tight"
            style={{ color: "var(--cli-text)" }}
          >
            Your accounts are safe
          </div>
          <div className="text-xs" style={{ color: "var(--cli-text-muted)" }}>
            Wonda acts like a human, feels like you
          </div>
        </div>
      </div>

      {/* Rate-limit gauges, split between reads and writes */}
      <div>
        <div
          className="mb-3 text-sm font-semibold tracking-tight"
          style={{ color: "var(--cli-text)" }}
        >
          Stay clear of rate limits
        </div>
        <div className="flex flex-col gap-3">
          {SAFETY_BARS.map((bar) => {
            const fill = (bar.value / bar.cap) * 100;
            return (
              <div key={bar.label}>
                <div className="mb-1.5 flex items-center justify-between">
                  <span
                    className="font-mono text-[11px] tracking-[0.1em] uppercase"
                    style={{ color: "var(--cli-text-muted)" }}
                  >
                    {bar.label}
                  </span>
                  <span
                    className="font-mono text-xs font-medium"
                    style={{ color: "var(--cli-text)" }}
                  >
                    <AnimatedNumber value={bar.value} /> / {bar.cap}
                  </span>
                </div>
                <div
                  className="relative h-2.5 w-full overflow-hidden"
                  style={{ background: "var(--hair)" }}
                >
                  <motion.div
                    className="h-full"
                    style={{ background: "var(--green)" }}
                    initial={{ width: animate ? "0%" : `${fill}%` }}
                    animate={{ width: `${fill}%` }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                  />
                  {/* Cap marker */}
                  <span
                    className="absolute top-0 bottom-0 w-px"
                    style={{ left: "100%", background: "var(--hair-strong)" }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-1.5">
          <HugeiconsIcon
            icon={CheckmarkBadge01Icon}
            className="size-4 text-[var(--green)]"
            strokeWidth={1.8}
          />
          <span className="text-xs" style={{ color: "var(--green)" }}>
            Within safe limits
          </span>
        </div>
      </div>

      {/* Bullet guarantees */}
      <ul className="flex flex-col gap-2">
        {SAFETY_POINTS.map((point) => (
          <li key={point} className="flex items-start gap-2.5">
            <CheckBullet />
            <span
              className="text-sm leading-relaxed"
              style={{ color: "var(--cli-text-secondary)" }}
            >
              {point}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stage 4 — Touchpoints journey revealing step by step               */
/* ------------------------------------------------------------------ */

type Touchpoint = {
  icon: IconSvgElement;
  label: string;
  side: "left" | "right";
  row: number; // 0 top, 1 middle, 2 bottom
};

// Every touchpoint an ICP sees of you, built up over weeks — arranged around
// "you" in the centre, three to each side.
const TOUCHPOINTS: Touchpoint[] = [
  { icon: UserAdd01Icon, label: "Connection request", side: "left", row: 0 },
  { icon: ThumbsUpIcon, label: "Likes, week after week", side: "left", row: 1 },
  { icon: Edit02Icon, label: "Posts in your voice", side: "left", row: 2 },
  { icon: ViewIcon, label: "Spots their pain points", side: "right", row: 0 },
  { icon: UserSearch01Icon, label: "Profile analyzed", side: "right", row: 1 },
  {
    icon: Message01Icon,
    label: "A DM, perfectly timed",
    side: "right",
    row: 2,
  },
];

// Vertical anchor (viewBox %) for each row, top to bottom.
const ROW_Y = [18, 50, 82];

type ResultStat = {
  value: number;
  label: string;
  prefix?: string;
  suffix?: string;
  accent?: boolean;
};

const RESULT_STATS: ResultStat[] = [
  { value: 96, label: "touchpoints" },
  { value: 52, label: "booking rates", prefix: "+", suffix: "%", accent: true },
  { value: 5, label: "saved per day", suffix: "h" },
];

function TouchpointsJourney({ animate }: { animate: boolean }) {
  return (
    <div className="flex h-full flex-col gap-4 p-6 sm:p-7">
      {/* Radial diagram — you in the centre, every touchpoint around you. */}
      <div className="relative flex-1" style={{ minHeight: "230px" }}>
        {/* Wires from the centre out to each touchpoint, with a pulse flowing
            outward (the connections you build over weeks). */}
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {TOUCHPOINTS.map((t, i) => {
            // ROW_Y / TOUCHPOINTS are fixed-length — index stays in bounds.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const yy = ROW_Y[t.row]!;
            const ex = t.side === "left" ? 30 : 70;
            const d = `M50 50 C 40 50, ${ex} 50, ${ex} ${yy}`;
            return (
              <g key={t.label}>
                <path
                  d={d}
                  stroke="var(--hair-strong)"
                  strokeWidth={1}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                />
                {animate && (
                  <motion.path
                    d={d}
                    stroke="var(--green)"
                    strokeWidth={1.4}
                    strokeLinecap="round"
                    strokeDasharray="3 9"
                    fill="none"
                    vectorEffect="non-scaling-stroke"
                    animate={{ strokeDashoffset: [0, -24] }}
                    transition={{
                      duration: 1.2,
                      ease: "linear",
                      repeat: Infinity,
                      delay: i * 0.14,
                    }}
                  />
                )}
              </g>
            );
          })}
        </svg>

        {/* Centre — your ICP. Only the circle is translated to 50/50 so its
            centre stays on the wire hub; the label hangs above it. */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <span
            className="absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 font-mono text-[10px] font-semibold tracking-[0.14em] whitespace-nowrap uppercase"
            style={{ color: "var(--green)" }}
          >
            Your ICP
          </span>
          <div className="relative">
            {animate && (
              <motion.div
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-1/2 size-20 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, color-mix(in srgb, var(--green) 45%, transparent), transparent 70%)",
                }}
                animate={{ opacity: [0.5, 0.9, 0.5], scale: [0.9, 1.06, 0.9] }}
                transition={{
                  duration: 2.6,
                  ease: "easeInOut",
                  repeat: Infinity,
                }}
              />
            )}
            <div
              className="relative flex size-14 items-center justify-center rounded-full border-2"
              style={{
                borderColor: "var(--green)",
                background: "var(--surface)",
              }}
            >
              <HugeiconsIcon
                icon={UserIcon}
                className="size-6 text-[var(--green)]"
                strokeWidth={1.8}
              />
            </div>
          </div>
        </div>

        {/* Touchpoints surrounding you */}
        {TOUCHPOINTS.map((t, i) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const yy = ROW_Y[t.row]!;
          const pos =
            t.side === "left"
              ? { right: "70%", top: `${yy}%` }
              : { left: "70%", top: `${yy}%` };
          return (
            <motion.div
              key={t.label}
              className={cn(
                "absolute flex -translate-y-1/2 items-center gap-2",
                t.side === "left" && "flex-row-reverse",
              )}
              style={pos}
              initial={animate ? { opacity: 0, scale: 0.8 } : false}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.4,
                delay: 0.15 + i * 0.1,
                ease: "easeOut",
              }}
            >
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-full border"
                style={{
                  borderColor: "var(--hair-strong)",
                  background: "var(--surface)",
                }}
              >
                <HugeiconsIcon
                  icon={t.icon}
                  className="size-4 text-[var(--green)]"
                  strokeWidth={1.7}
                />
              </span>
              <span
                className={cn(
                  "text-[11px] leading-tight whitespace-nowrap",
                  t.side === "left" ? "text-right" : "text-left",
                )}
                style={{ color: "var(--cli-text-secondary)" }}
              >
                {t.label}
              </span>
            </motion.div>
          );
        })}
      </div>

      {/* Result row — animated counters land the payoff */}
      <motion.div
        className="grid grid-cols-3 gap-px overflow-hidden border border-[var(--hair)] bg-[var(--hair)]"
        initial={animate ? { opacity: 0, y: 12 } : false}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.45,
          delay: TOUCHPOINTS.length * 0.12,
          ease: "easeOut",
        }}
      >
        {RESULT_STATS.map((stat) => (
          <div
            key={stat.label}
            className="flex flex-col items-center gap-0.5 bg-[var(--surface)] px-2 py-3 text-center"
          >
            <span
              className="flex items-center gap-1 text-lg font-semibold tracking-tight"
              style={{
                color: stat.accent ? "var(--green)" : "var(--cli-text)",
              }}
            >
              {stat.accent && (
                <HugeiconsIcon
                  icon={ArrowUpRight01Icon}
                  className="size-4"
                  strokeWidth={2}
                />
              )}
              <AnimatedNumber
                value={stat.value}
                prefix={stat.prefix}
                suffix={stat.suffix}
              />
            </span>
            <span
              className="text-[10px] leading-tight"
              style={{ color: "var(--cli-text-muted)" }}
            >
              {stat.label}
            </span>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main section                                                       */
/* ------------------------------------------------------------------ */

export function WhyWonda() {
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  // Once the visitor clicks a tab, stop rotating entirely — jumping them to the
  // next tab after a click is disorienting.
  const [stopped, setStopped] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // Only begin rotating (and animating the counters inside each stage) once the
  // panel scrolls into view, so the visitor always sees it start from 01.
  const inView = useInView(panelRef, { amount: 0.4 });

  // Auto-advance tabs; resets on every manual selection (effect deps on active),
  // pauses while hovering, halts after a click, and waits until in view.
  useEffect(() => {
    if (paused || reduced || stopped || !inView) return;
    const id = setTimeout(
      () => setActive((a) => (a + 1) % POINTS.length),
      AUTO_ADVANCE_MS,
    );
    return () => clearTimeout(id);
  }, [active, paused, reduced, stopped, inView]);

  const selectTab = (i: number) => {
    setStopped(true);
    setActive(i);
  };

  // POINTS is fixed-length — active stays in bounds via modulo.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const activeStage = POINTS[active]!.id;

  return (
    <section
      id="why-wonda"
      className="scroll-mt-16 px-4 py-14 sm:px-6 md:py-20"
    >
      <motion.div
        className="mx-auto max-w-5xl"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="mx-auto max-w-2xl text-center">
          <div className="flex justify-center">
            <SectionEyebrow index="02">Why Wonda</SectionEyebrow>
          </div>
          <h2
            className="mt-5 text-2xl font-semibold tracking-[-0.03em] text-balance sm:text-3xl lg:text-4xl"
            style={{ color: "var(--cli-text)" }}
          >
            Built to automate, designed to look human
          </h2>
        </div>

        {/* Tabbed control panel */}
        <div
          ref={panelRef}
          className="mt-10 grid gap-px overflow-hidden border border-[var(--hair)] bg-[var(--hair)] lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Left: vertical tab list */}
          <div className="flex flex-col bg-[var(--surface)]">
            {POINTS.map((point, i) => {
              const isActive = i === active;
              return (
                <button
                  key={point.id}
                  type="button"
                  onClick={() => selectTab(i)}
                  aria-pressed={isActive}
                  className={cn(
                    "relative border-l-2 px-5 py-5 text-left transition-colors sm:px-6",
                    i > 0 && "border-t border-t-[var(--hair)]",
                  )}
                  style={{
                    borderLeftColor: isActive ? "var(--green)" : "transparent",
                    background: isActive ? GREEN_TINT : "transparent",
                  }}
                >
                  <div className="flex items-baseline gap-3">
                    <span
                      className="font-mono text-[11px] font-medium tracking-[0.12em]"
                      style={{
                        color: isActive
                          ? "var(--green)"
                          : "var(--cli-text-muted)",
                      }}
                    >
                      {point.num}
                    </span>
                    <span
                      className="text-[15px] font-semibold tracking-tight transition-colors"
                      style={{
                        color: isActive
                          ? "var(--cli-text)"
                          : "var(--cli-text-secondary)",
                      }}
                    >
                      {point.title}
                    </span>
                  </div>
                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        key="body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="overflow-hidden"
                      >
                        <p
                          className="mt-2 cursor-text pl-[calc(0.75rem+1.6ch)] text-sm leading-relaxed select-text"
                          style={{ color: "var(--cli-text-secondary)" }}
                        >
                          {point.body}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {/* Auto-advance progress bar — only while actually rotating */}
                  {isActive && !paused && !reduced && !stopped && inView && (
                    <motion.span
                      key={`bar-${active}`}
                      className="absolute bottom-0 left-0 h-px"
                      style={{ background: "var(--green)" }}
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{
                        duration: AUTO_ADVANCE_MS / 1000,
                        ease: "linear",
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Right: stage */}
          <div className="flex min-h-[380px] flex-col bg-[var(--surface)]">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStage}
                className="flex h-full flex-1 flex-col"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                {activeStage === "schema" && <SchemaStage animate={!reduced} />}
                {activeStage === "capabilities" && <CapabilitiesStage />}
                {activeStage === "safety" && <SafetyStage animate={!reduced} />}
                {activeStage === "result" && (
                  <TouchpointsJourney animate={!reduced} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
